<template>
  <div class="survey-card">
    <div class="survey-header">
      <h2>Проведение опроса</h2>
      <div class="progress">
        <span>Участник {{ completedRespondents.length + (currentRespondent ? 1 : 0) }} из {{ survey.participants.length }}</span>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
      </div>
    </div>

    <div v-if="result" class="result-summary">
      <h3>Опрос завершён</h3>
      <div v-if="notPassed.length" class="result-summary__block">
        <div class="result-summary__label">Не прошли:</div>
        <div class="result-summary__list">
          <span v-for="p in notPassed" :key="p.id" class="result-summary__chip">{{ p.full_name }}</span>
        </div>
      </div>
      <div v-else class="result-summary__block">
        <div class="result-summary__label">Все участники прошли</div>
      </div>
    </div>

    <div v-if="!result && !currentRespondent" class="loading">
      Подготовка опроса...
    </div>

    <!-- Опрос участника -->
    <div v-else-if="!result" class="survey-questions">
      <div class="respondent-header">
        <h3>Опрашиваемый: {{ currentRespondent.full_name }}</h3>

        <div class="respondent-picker">
          <span class="respondent-picker__label">Выбрать участника:</span>
          <select
            class="respondent-picker__select"
            :value="currentRespondent?.id || ''"
            @change="onRespondentSelect($event.target.value)"
          >
            <option value="">—</option>
            <option
              v-for="p in remainingParticipants"
              :key="p.id"
              :value="p.id"
            >
              {{ p.full_name }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="questionSetLoading" class="loading">
        Загрузка вопросов...
      </div>

      <div v-else-if="questions.length === 0" class="empty">
        В выбранном наборе нет вопросов
      </div>

      <div v-if="currentQuestion" class="question-block">
        <div class="question-meta">
          <div class="question-counter">Вопрос {{ currentQuestionIndex + 1 }} из {{ questions.length }}</div>
        </div>
        <h4 class="question-text">{{ currentQuestion.text }}</h4>

        <div class="choices-container single">
          <div class="choices-column">
            <div class="choices-list">
              <div 
                v-for="user in otherParticipants"
                :key="user.id"
                class="choice-item"
              >
                <input 
                  type="checkbox"
                  :id="`q-${currentQuestion.id}-${user.id}`"
                  :value="user.id"
                  v-model="selectedChoices[currentQuestion.id]"
                />
                <label :for="`q-${currentQuestion.id}-${user.id}`">
                  {{ user.full_name }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="survey-actions right">
          <button
            @click="onNextQuestion"
            class="btn-primary"
            :disabled="loading"
          >
            {{ isLastQuestion ? 'Завершить' : 'Далее' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Завершение -->
    <div v-if="!result && completedRespondents.length === survey.participants.length" class="completion">
      <h3>✓ Все участники опрошены!</h3>
      <button @click="completesurvey" class="btn-primary">
        Завершить опрос и рассчитать результаты
      </button>
    </div>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { submitChoice, completeSurvey, getQuestionSet } from '@/api/mpo'

export default {
  name: 'StarChartSurvey',
  props: {
    survey: {
      type: Object,
      required: true,
    },
    result: {
      type: Object,
      default: null,
    },
  },
  emits: ['survey-completed'],
  data() {
    return {
      currentRespondent: null,
      completedRespondents: [],
      currentQuestionIndex: 0,
      selectedChoices: {},
      error: null,
      loading: false,
      questionSet: null,
      questionSetLoading: false,
    }
  },
  computed: {
    effectiveQuestionSet() {
      if (this.survey?.question_set && typeof this.survey.question_set === 'object') {
        return this.survey.question_set
      }
      return this.questionSet
    },
    questions() {
      return this.effectiveQuestionSet?.questions || []
    },
    otherParticipants() {
      return this.survey.participants.filter(p => p.id !== this.currentRespondent?.id)
    },
    currentQuestion() {
      return this.questions[this.currentQuestionIndex] || null
    },
    isLastQuestion() {
      return this.questions.length > 0 && this.currentQuestionIndex >= this.questions.length - 1
    },
    notPassed() {
      return (this.result && Array.isArray(this.result.not_passed)) ? this.result.not_passed : []
    },
    remainingParticipants() {
      if (!this.survey || !Array.isArray(this.survey.participants)) return []
      return this.survey.participants.filter(p => !this.completedRespondents.includes(p.id))
    },
    progressPercent() {
      return Math.round((this.completedRespondents.length / this.survey.participants.length) * 100)
    },
  },
  watch: {
    survey: {
      immediate: true,
      handler() {
        this.ensureQuestionSetLoaded()
        this.resetLocalState()
        this.loadProgress()
        if (!this.restoreCurrentRespondentIfAny()) {
          this.selectNextRespondent()
        }
      },
    },
    questionSet() {
      if (this.currentRespondent) {
        this.initializeChoices()
      }
      if (!this.currentRespondent) {
        this.selectNextRespondent()
      }
    },
  },
  mounted() {
    this.ensureQuestionSetLoaded()
    // If watcher hasn't run yet (edge-case), still try restore
    this.loadProgress()
    if (!this.restoreCurrentRespondentIfAny()) {
      this.selectNextRespondent()
    }
  },
  methods: {
    storageBase() {
      const id = this.survey?.id
      return id ? `mpo:survey:${id}` : 'mpo:survey:unknown'
    },
    progressKey() {
      return `${this.storageBase()}:completedRespondents`
    },
    currentKey() {
      return `${this.storageBase()}:currentRespondentId`
    },
    loadProgress() {
      try {
        const raw = localStorage.getItem(this.progressKey())
        const arr = raw ? JSON.parse(raw) : []
        if (Array.isArray(arr)) {
          this.completedRespondents = arr
            .map((x) => Number(x))
            .filter((n) => Number.isFinite(n) && n > 0)
        }
      } catch {
        // ignore
      }
    },
    saveProgress() {
      try {
        localStorage.setItem(this.progressKey(), JSON.stringify(this.completedRespondents))
      } catch {
        // ignore
      }
    },
    saveCurrentRespondent() {
      try {
        const id = this.currentRespondent?.id
        if (id) localStorage.setItem(this.currentKey(), String(id))
        else localStorage.removeItem(this.currentKey())
      } catch {
        // ignore
      }
    },
    restoreCurrentRespondentIfAny() {
      try {
        const raw = localStorage.getItem(this.currentKey())
        const id = raw ? parseInt(raw, 10) : NaN
        if (!Number.isFinite(id) || id <= 0) return false
        if (this.completedRespondents.includes(id)) return false
        const p = this.survey.participants.find((x) => x.id === id)
        if (!p) return false
        this.selectRespondent(p)
        return true
      } catch {
        return false
      }
    },
    resetLocalState() {
      this.currentRespondent = null
      this.completedRespondents = []
      this.currentQuestionIndex = 0
      this.selectedChoices = {}
      this.error = null
    },
    async ensureQuestionSetLoaded() {
      const qs = this.survey?.question_set
      if (!qs || typeof qs === 'object' || this.questionSetLoading) return
      if (typeof qs !== 'number') return

      this.questionSetLoading = true
      try {
        const loaded = await getQuestionSet(qs)
        this.questionSet = loaded
      } catch (e) {
        console.error(e)
      } finally {
        this.questionSetLoading = false
      }
    },
    selectRespondent(participant) {
      this.currentRespondent = participant
      this.initializeChoices()
      this.saveCurrentRespondent()
    },
    selectNextRespondent() {
      if (this.result) return
      if (!this.survey || !Array.isArray(this.survey.participants)) return
      const next = this.survey.participants.find(p => !this.completedRespondents.includes(p.id))
      if (!next) {
        this.currentRespondent = null
        this.saveCurrentRespondent()
        return
      }
      this.currentRespondent = next
      if (this.questions.length) {
        this.initializeChoices()
      }
      this.saveCurrentRespondent()
    },
    onRespondentSelect(value) {
      const id = parseInt(String(value || ''), 10)
      if (!Number.isFinite(id) || id <= 0) return
      const p = this.survey.participants.find((x) => x.id === id)
      if (!p) return
      if (this.completedRespondents.includes(p.id)) return
      this.selectRespondent(p)
    },
    initializeChoices() {
      this.currentQuestionIndex = 0
      this.selectedChoices = {}
      this.questions.forEach(q => {
        this.selectedChoices[q.id] = []
      })
    },
    normalizeChoiceType(value) {
      const v = String(value || '').toLowerCase().trim()
      if (v === 'minus' || v === '-') return 'minus'
      return 'plus'
    },
    async saveRespondentChoices() {
      this.error = null
      this.loading = true

      try {
        // Сохраняем все выборы (тип берём из вопроса)
        for (const question of this.questions) {
          const qId = question.id
          const choiceType = this.normalizeChoiceType(question.choice_type)
          const selected = this.selectedChoices[qId] || []

          for (let order = 0; order < selected.length; order++) {
            const chosenId = selected[order]
            const chosenUser = this.survey.participants.find(p => p.id === chosenId)
            await submitChoice(this.survey.id, {
              respondent_id: this.currentRespondent.id,
              respondent_name: this.currentRespondent.full_name,
              question_id: qId,
              chosen_user_id: chosenId,
              chosen_user_name: chosenUser?.full_name || '',
              choice_type: choiceType,
              order: order + 1,
            })
          }
        }

        this.completedRespondents.push(this.currentRespondent.id)
        this.saveProgress()
        this.currentRespondent = null
        this.saveCurrentRespondent()
        this.selectNextRespondent()
      } catch (err) {
        this.error = 'Ошибка при сохранении выборов'
        console.error(err)
      } finally {
        this.loading = false
      }
    },
    onNextQuestion() {
      this.error = null
      if (!this.currentQuestion) return

      const selected = this.selectedChoices[this.currentQuestion.id] || []
      if (!selected.length) {
        this.error = 'Выберите хотя бы одного участника'
        return
      }

      if (this.isLastQuestion) {
        this.saveRespondentChoices()
        return
      }

      this.currentQuestionIndex = Math.min(this.currentQuestionIndex + 1, this.questions.length - 1)
    },
    async completesurvey() {
      this.loading = true
      try {
        const result = await completeSurvey(this.survey.id)
        this.$emit('survey-completed', result)

        try {
          localStorage.removeItem(this.progressKey())
          localStorage.removeItem(this.currentKey())
        } catch { }
      } catch (err) {
        this.error = 'Ошибка при завершении опроса'
        console.error(err)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.survey-card {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.survey-header h2 {
  margin: 0 0 1rem 0;
  color: #1a202c;
}

.progress {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.progress span {
  font-size: 0.9rem;
  color: #6b7280;
}

.progress-bar {
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #2563eb;
  transition: width 0.3s ease;
}

.respondent-select h3 {
  margin-bottom: 1rem;
  color: #1a202c;
}

.respondent-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.respondent-picker {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.respondent-picker__label {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 600;
}

.respondent-picker__select {
  height: 34px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  font-size: 0.95rem;
}

.respondent-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.respondent-btn {
  padding: 1rem;
  background: #f3f4f6;
  border: 2px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
}

.respondent-btn:hover:not(:disabled) {
  background: #e5e7eb;
  border-color: #2563eb;
}

.respondent-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.badge {
  position: absolute;
  top: 5px;
  right: 5px;
  background: #22c55e;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
}

.respondent-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
}

.respondent-header h3 {
  margin: 0;
  color: #1a202c;
}

.btn-back {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  font-size: 0.9rem;
  text-decoration: underline;
}

.survey-questions {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.question-block {
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 4px;
  border-left: 4px solid #2563eb;
}

.question-meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
  color: #6b7280;
  font-size: 0.9rem;
}

.choices-container.single {
  display: block;
}

.question-text {
  margin: 0 0 1rem 0;
  color: #1a202c;
  font-size: 1.6rem;
  font-weight: 700;
}

.survey-actions.right {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 1rem;
}

.result-summary {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
}

.result-summary h3 {
  margin: 0 0 0.75rem 0;
  color: #1a202c;
}

.result-summary__block {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.result-summary__label {
  font-weight: 600;
  color: #374151;
}

.result-summary__list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.result-summary__chip {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.9rem;
  color: #111827;
}

.choices-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.choices-column {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.choice-label {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
}

.choices-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
}

.choice-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.choice-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.choice-item label {
  cursor: pointer;
  font-size: 0.9rem;
}

.survey-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-secondary {
  background: #e5e7eb;
  color: #374151;
}

.btn-secondary:hover {
  background: #d1d5db;
}

.completion {
  text-align: center;
  padding: 2rem;
  background: #eff6ff;
  border-radius: 4px;
}

.completion h3 {
  margin: 0 0 1rem 0;
  color: #1e40af;
  font-size: 1.1rem;
}

.error-message {
  background: #fee;
  color: #c33;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
}
</style>